import React from "react";
import CrudManager from "../../common/CrudManager";
import { Database, Layers } from "lucide-react";

const BLOCK_TYPE_LABELS = {
  badge: { label: "Badge", color: "bg-blue-100 text-blue-700" },
  stat: { label: "Stat", color: "bg-purple-100 text-purple-700" },
  step: { label: "Step", color: "bg-green-100 text-green-700" },
  node: { label: "Node", color: "bg-orange-100 text-orange-700" },
  feature: { label: "Feature", color: "bg-pink-100 text-pink-700" },
  "trust-indicator": { label: "Trust", color: "bg-teal-100 text-teal-700" },
};

const ContentBlockManager = ({ sectionId, onClose }) => {
  const fields = [
    {
      name: "blockType",
      label: "Block Type",
      type: "select",
      options: [
        { value: "badge", label: "Trust Badge" },
        { value: "stat", label: "Statistic" },
        { value: "step", label: "Process Step" },
        { value: "node", label: "Diagram Node" },
        { value: "feature", label: "Feature Item" },
        { value: "trust-indicator", label: "Trust Indicator" },
      ],
      required: true,
      hideOnEdit: true, // Can't change type after creation
    },
    {
      name: "identifier",
      label: "Identifier (Optional)",
      type: "text",
      placeholder: "e.g., 'placed', 'rate' - for programmatic access",
      required: false,
    },
    {
      name: "labelEn",
      label: "Label (English)",
      type: "text",
      placeholder: "e.g., Talent Placed",
      required: true,
    },
    {
      name: "labelJa",
      label: "Label (Japanese)",
      type: "text",
      placeholder: "例：人材配置",
      required: false,
    },
    {
      name: "valueEn",
      label: "Value (English)",
      type: "text",
      placeholder: "e.g., '1,500+' or 'SSL Secured'",
      required: false,
    },
    {
      name: "valueJa",
      label: "Value (Japanese)",
      type: "text",
      placeholder: "例：1,500+",
      required: false,
    },
    {
      name: "descriptionEn",
      label: "Description (English)",
      type: "textarea",
      placeholder: "Detailed description...",
      required: false,
      fullWidth: true,
    },
    {
      name: "descriptionJa",
      label: "Description (Japanese)",
      type: "textarea",
      placeholder: "詳細な説明...",
      required: false,
      fullWidth: true,
    },
    {
      name: "iconName",
      label: "Icon Name (Lucide)",
      type: "text",
      placeholder: "e.g., Users, Database, CheckCircle",
      required: false,
    },
    {
      name: "imageUrl",
      label: "Image URL (Optional)",
      type: "text",
      placeholder: "/uploads/icons/badge.png",
      required: false,
    },
    {
      name: "linkUrl",
      label: "Link URL (Optional)",
      type: "text",
      placeholder: "https://example.com",
      required: false,
    },
    {
      name: "order",
      label: "Display Order",
      type: "text",
      placeholder: "0",
      required: false,
    },
    {
      name: "isPublished",
      label: "Published",
      type: "select",
      options: [
        { value: "true", label: "Published" },
        { value: "false", label: "Draft" },
      ],
      required: false,
    },
  ];

  const formatCreateData = (formData) => ({
    sectionId,
    blockType: formData.blockType,
    identifier: formData.identifier?.trim() || undefined,
    labelEn: formData.labelEn.trim(),
    labelJa: formData.labelJa?.trim() || undefined,
    valueEn: formData.valueEn?.trim() || undefined,
    valueJa: formData.valueJa?.trim() || undefined,
    descriptionEn: formData.descriptionEn?.trim() || undefined,
    descriptionJa: formData.descriptionJa?.trim() || undefined,
    iconName: formData.iconName?.trim() || undefined,
    imageUrl: formData.imageUrl?.trim() || undefined,
    linkUrl: formData.linkUrl?.trim() || undefined,
    order: formData.order ? parseInt(formData.order, 10) : 0,
    isPublished: formData.isPublished !== "false",
  });

  const formatUpdateData = (formData) => ({
    identifier: formData.identifier?.trim() || undefined,
    labelEn: formData.labelEn.trim(),
    labelJa: formData.labelJa?.trim() || undefined,
    valueEn: formData.valueEn?.trim() || undefined,
    valueJa: formData.valueJa?.trim() || undefined,
    descriptionEn: formData.descriptionEn?.trim() || undefined,
    descriptionJa: formData.descriptionJa?.trim() || undefined,
    iconName: formData.iconName?.trim() || undefined,
    imageUrl: formData.imageUrl?.trim() || undefined,
    linkUrl: formData.linkUrl?.trim() || undefined,
    order: formData.order ? parseInt(formData.order, 10) : 0,
    isPublished: formData.isPublished !== "false",
  });

  const renderItem = (item, onEdit, onDelete) => {
    const typeMeta = BLOCK_TYPE_LABELS[item.blockType] || {
      label: item.blockType,
      color: "bg-slate-100 text-slate-600",
    };

    return (
      <div className="flex items-start justify-between w-full group gap-4">
        <div className="flex items-start space-x-4 flex-1 min-w-0">
          <div className="w-12 h-12 bg-subidha-blue/5 rounded-2xl flex items-center justify-center border border-subidha-blue/10 group-hover:bg-subidha-blue transition-all duration-300 shrink-0">
            <Database className="w-5 h-5 text-subidha-blue group-hover:text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <h3 className="text-sm font-black text-slate-900">
                {item.labelEn}
              </h3>
              <span
                className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${typeMeta.color}`}
              >
                {typeMeta.label}
              </span>
              {item.identifier && (
                <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                  {item.identifier}
                </span>
              )}
            </div>
            {item.valueEn && (
              <p className="text-xs text-subidha-blue font-bold mb-1">
                {item.valueEn}
              </p>
            )}
            {item.descriptionEn && (
              <p className="text-xs text-slate-500 truncate">
                {item.descriptionEn}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-1 shrink-0">
          <span className="text-[10px] font-black text-slate-300 mr-2">
            #{item.order ?? 0}
          </span>
          {onEdit && (
            <button
              onClick={() => onEdit(item)}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
              title="Edit"
            >
              <Layers className="w-4 h-4 text-slate-400" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(item)}
              className="p-2 hover:bg-red-50 rounded-xl transition-colors"
              title="Delete"
            >
              <span className="w-4 h-4 text-red-400">×</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <CrudManager
      title="Manage Content Blocks"
      apiEndpoint={
        sectionId ? `/content-blocks?sectionId=${sectionId}` : "/content-blocks"
      }
      fields={fields}
      renderItem={renderItem}
      onClose={onClose}
      icon={Database}
      emptyMessage="No content blocks yet. Add your first block!"
      formatCreateData={formatCreateData}
      formatUpdateData={formatUpdateData}
    />
  );
};

export default ContentBlockManager;
